/* cwclientelements
   copyright 2019 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import * as React from 'react';
import {providers} from '@logilab/cwclientlibjs';
import {DefaultEntityRelation} from './DefaultEntityRelation';

/**
 * Base componentn for a group of entity relation
 */
export class EntityRelationsGroup<
    P extends EntityRelationsGroupProps = EntityRelationsGroupProps,
    S extends {} = {}
> extends React.Component<P, S> {}

/**
 * The parameters for relations group components
 */
export interface EntityRelationsGroupProps {
    /**
     * The entity
     */
    entity: providers.Entity;
    /**
     * The shema for the relations in the group
     */
    relations: providers.EntityRelationSchema[];
    /**
     * Whether the entity is the relation's subject
     */
    entityIsSubject: boolean;
    /**
     * Whether to enable the edition of the relations
     */
    editable: boolean;
    /**
     * The CSS class name(s) for the component in normal conditions
     */
    className: string;
    /**
     * Handler of events when the user click on a link to an entity
     */
    onClickGotoTarget: (eid: number) => void;
    /**
     * Handler of events when the user wants to edit the relations
     */
    onClickEdit: (relations: providers.EntityRelationSchema[]) => void;
    /**
     * Gets the target link for the specified entity
     * @param eid The entity
     */
    getTargetLink: (eid: number) => string;
    /**
     * Gets the displayable value for the specified entity and schema
     * @param entity The entity that will be rendered
     * @param schema The schema for the entity
     */
    renderDisplayValue: (
        entity: providers.Entity,
        schema: providers.EntitySchema
    ) => JSX.Element;
    /**
     * Renders a specific relation in the group
     * @param component The current component (this)
     * @param relation The relation to render
     */
    renderRelation: (
        component: EntityRelationsGroup,
        relation: providers.EntityRelationSchema
    ) => JSX.Element;
    /**
     * Renders the component
     * @param component The current component (this)
     */
    render: (component: EntityRelationsGroup) => JSX.Element;
}

/**
 * The values for the default parameters of relations components
 */
export const ENTITY_RELATIONS_GROUP_DEFAULT_PROPS = {
    editable: false,
    className: '',
    onClickGotoTarget: (eid: number) => {
        /* do nothing */
    },
    onClickEdit: (relations: providers.EntityRelationSchema[]) => {
        /* do nothing */
    },
    getTargetLink: (eid: number) => `/${eid}`,
    renderDisplayValue: (
        entity: providers.Entity,
        schema: providers.EntitySchema
    ) => {
        return <span>{providers.getTitleFor(entity, schema)}</span>;
    },
    renderRelation: (
        component: EntityRelationsGroup,
        relation: providers.EntityRelationSchema
    ) => {
        return (
            <DefaultEntityRelation
                key={`${
                    component.props.entity['@eid']
                }_${providers.getUniqueRelationName(relation)}`}
                entity={component.props.entity}
                relation={relation}
                entityIsSubject={component.props.entityIsSubject}
                editable={component.props.editable}
                className={component.props.className}
                onClickGotoTarget={component.props.onClickGotoTarget}
                onClickEdit={(relation) =>
                    component.props.onClickEdit([relation])
                }
                getTargetLink={component.props.getTargetLink}
                renderDisplayValue={component.props.renderDisplayValue}
            />
        );
    },
    render: (component: EntityRelationsGroup) => {
        return (
            <div>
                {component.props.relations.map(
                    (relation: providers.EntityRelationSchema) =>
                        component.props.renderRelation(component, relation)
                )}
            </div>
        );
    },
};
